import { useEffect, useState } from "react";
import { Check, Loader2, Unplug, Send, RefreshCw, ShieldCheck } from "lucide-react";
import { api, errorMessage } from "../api";
import AlertsSetup from "./AlertsSetup";

const ST = { APPROVED: "success", PENDING: "accent", REJECTED: "mod-cancel", ERROR: "mod-cancel", NOT_FOUND: "neutral" };

function ConnectForm({ onSaved }) {
  const [f, setF] = useState({ phone_number_id: "", waba_id: "", access_token: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { onSaved((await api.put("/whatsapp/connection", f)).data); } catch (e2) { setErr(errorMessage(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="wa-form" data-testid="wa-connect-form">
      <label>Phone Number ID<input required inputMode="numeric" value={f.phone_number_id} onChange={(e) => setF({ ...f, phone_number_id: e.target.value.trim() })} data-testid="wa-phone-number-id" /></label>
      <label>WhatsApp Business Account (WABA) ID<input required inputMode="numeric" value={f.waba_id} onChange={(e) => setF({ ...f, waba_id: e.target.value.trim() })} data-testid="wa-waba-id" /></label>
      <label>Permanent access token (System User)<input required type="password" autoComplete="off" value={f.access_token} onChange={(e) => setF({ ...f, access_token: e.target.value })} data-testid="wa-access-token" /></label>
      {err && <div className="auth-alert error" data-testid="wa-connect-error">{err}</div>}
      <button className="primary-button full" disabled={busy} data-testid="wa-connect-submit">{busy ? <Loader2 className="spin" size={14} /> : <ShieldCheck size={14} />} Verify with Meta &amp; connect</button>
      <p className="form-hint">Find these in Meta Business Suite → WhatsApp Manager → API Setup. Create a System User token with whatsapp_business_messaging and whatsapp_business_management. The token is encrypted and never shown again.</p>
    </form>
  );
}

export default function WhatsAppConnect({ branch }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/whatsapp/connection").then(({ data }) => setC(data)).catch((e) => setMsg(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const run = async (key, fn) => {
    setBusy(key); setMsg("");
    try { await fn(); } catch (e) { setMsg(errorMessage(e)); } finally { setBusy(""); }
  };
  const disconnect = () => run("dc", async () => { if (!window.confirm("Disconnect WhatsApp? Alerts will go by SMS only (if MSG91 is connected).")) return; setC((await api.delete("/whatsapp/connection")).data); });
  const submit = () => run("sub", async () => { await api.post("/whatsapp/templates/submit", { app_origin: window.location.origin }); await load(); setMsg("Templates submitted to Meta for approval"); });
  const refresh = () => run("ref", async () => { await api.post("/whatsapp/templates/refresh"); await load(); });
  const test = (kind) => run(`t-${kind}`, async () => {
    const { data } = await api.post(`/whatsapp/test?branch=${branch}`, { kind });
    setMsg(data.deliveries.map((d) => `${d.phone}: ${d.status}${d.channel ? ` via ${d.channel}` : ""}${d.error ? " – " + d.error : ""}`).join(" · "));
  });
  if (!c) return <div className="panel">{msg || "Loading…"}</div>;
  return (
    <div className="dg-layout" data-testid="wa-page">
      <div className="panel">
        <div className="panel-head"><div><span className="overline">META WHATSAPP CLOUD API · {c.graph_version}</span><h2>Your WhatsApp Business number</h2></div>
          <span className={`pill ${c.connected ? "success" : "mod-note"}`} data-testid="wa-mode">{c.connected ? "LIVE" : "NOT CONNECTED"}</span></div>
        {c.connected ? (
          <div className="wa-info" data-testid="wa-connected-info">
            <p><b>{c.verified_name || "Business"}</b> · {c.display_phone}</p>
            <p>Phone Number ID {c.phone_number_id} · WABA {c.waba_id} · token {c.token_hint}</p>
            <button className="secondary-button" onClick={disconnect} disabled={busy === "dc"} data-testid="wa-disconnect"><Unplug size={13} /> Disconnect</button>
          </div>
        ) : (
          <>
            <p className="form-hint" data-testid="wa-mock-hint">Not connected. Every message goes out from <b>your own</b> verified WhatsApp Business number, never a shared FynDine number. Until you connect, alerts use your MSG91 SMS (if set up).</p>
            <ConnectForm onSaved={(d) => { setC(d); setMsg("Connected and verified with Meta"); }} />
          </>
        )}
      </div>
      <div className="panel" data-testid="wa-templates">
        <div className="panel-head"><div><span className="overline">MESSAGE TEMPLATES · UTILITY</span><h2>Approval by Meta</h2></div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="secondary-button" onClick={submit} disabled={!c.connected || busy === "sub"} data-testid="wa-submit-templates">{busy === "sub" ? <Loader2 className="spin" size={13} /> : <Send size={13} />} Submit for approval</button>
            <button className="icon-button" onClick={refresh} disabled={!c.connected || busy === "ref"} data-testid="wa-refresh-templates"><RefreshCw size={14} /></button>
          </div></div>
        {Object.entries(c.template_defs).map(([k, t]) => {
          const st = c.templates?.[k];
          return (
            <div className="wa-tpl" key={k} data-testid={`wa-template-${k}`}>
              <div><b>{t.name}</b><span className={`pill ${ST[st?.status] || "neutral"}`} data-testid={`wa-template-status-${k}`}>{st?.status || "NOT SUBMITTED"}</span></div>
              <pre>{t.body}</pre>
              {t.path && <small>Button → {window.location.origin}/{t.path}/&#123;&#123;1&#125;&#125;</small>}
              {st?.error && <small className="err">{st.error}</small>}
              {st?.rejected_reason && st.rejected_reason !== "NONE" && <small className="err">Rejected: {st.rejected_reason}</small>}
            </div>
          );
        })}
        <div className="wa-test">
          <button className="primary-button" onClick={() => test("daily")} disabled={busy === "t-daily"} data-testid="wa-test-daily">{busy === "t-daily" ? <Loader2 className="spin" size={14} /> : <Check size={14} />} Send test daily digest</button>
          <button className="secondary-button" onClick={() => test("weekly")} disabled={busy === "t-weekly"} data-testid="wa-test-weekly">Send test weekly report</button>
        </div>
        {msg && <small className="dg-msg" data-testid="wa-msg">{msg}</small>}
      </div>
      <AlertsSetup />
    </div>
  );
}
